/**
 * The records a task is linked to: contacts, companies, groups and temp
 * workers, as `links: [{ type, id, label, sub? }]`. A contact link also
 * carries its `companyId`, a temp worker's its `validated` flag.
 *
 * A real task keeps its `links` (api); only a task created before them has
 * none, and its links are drawn from its company, group and contact name.
 */

export const LINK_TYPES = ['company', 'group', 'contact', 'tempWorker'];

// A contact is known by its company too: the same person may belong to two.
export const linkKey = (link) =>
  link.type === 'contact'
    ? `contact:${link.companyId ?? ''}:${link.id}`
    : `${link.type}:${link.id}`;

// An id of Jobbiz (an ObjectId), not one of the demo.
export const isRealId = (id) =>
  typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);

// The links of a task without `links`: its contact (which names its company),
// else its company, else its group, as the detail showed them so far.
const legacyLinks = (task) => {
  const company = task.company?.id
    ? { type: 'company', id: task.company.id, label: task.company.name ?? '' }
    : null;
  const group = task.group?.id
    ? { type: 'group', id: task.group.id, label: task.group.name ?? '' }
    : null;
  const contact = task.contactName
    ? {
        type: 'contact',
        id: `name:${task.contactName}`,
        label: task.contactName,
        sub: task.company?.name ?? '',
        companyId: task.company?.id ?? null,
      }
    : null;
  if (contact) return company || !group ? [contact] : [group, contact];
  return company ? [company] : group ? [group] : [];
};

/** Every link of a task, for its detail. */
export const taskLinks = (task) =>
  task.links?.length ? task.links : legacyLinks(task);

/**
 * The links a row of the list or a card of the kanban names: the first one,
 * then « +N ». A task without `links` shows its company, else its group, as
 * before (its contact is the person at that company, not another record).
 */
export const rowLinks = (task) => {
  if (task.links?.length) return task.links;
  if (task.company?.id)
    return [
      { type: 'company', id: task.company.id, label: task.company.name ?? '' },
    ];
  if (task.group?.id)
    return [{ type: 'group', id: task.group.id, label: task.group.name ?? '' }];
  return [];
};

/**
 * The company or group page a link leads to, as the tasks page opens it
 * (`{ kind, id, name }`), or null: a temp worker has no such page, a contact
 * leads to its company.
 */
export const recordOfLink = (link) => {
  if (link.type === 'company')
    return { kind: 'company', id: link.id, name: link.label };
  if (link.type === 'group')
    return { kind: 'group', id: link.id, name: link.label };
  if (link.type === 'contact' && link.companyId)
    return { kind: 'company', id: link.companyId, name: link.sub ?? '' };
  return null;
};

/**
 * The choices of the form, from admin/crm/contacts (`contacts`: an option
 * per group, company and company member) and the temp workers.
 */
export const linkOptionsOf = (contacts, tempWorkers = []) => [
  ...contacts.map((option) => {
    if (option.contact) {
      return {
        type: 'contact',
        // The demo's options have no userId: their key stands in for it.
        id: option.userId ?? option.key,
        label: option.contact,
        sub: option.company ?? '',
        companyId: option.companyId ?? null,
      };
    }
    if (option.companyId)
      return {
        type: 'company',
        id: option.companyId,
        label: option.label,
        sub: option.city ?? '',
      };
    return { type: 'group', id: option.groupId, label: option.label };
  }),
  ...tempWorkers.map((worker) => ({
    type: 'tempWorker',
    id: worker.id,
    label: `${worker.firstname} ${worker.lastname}`.trim(),
    sub: worker.city ?? '',
    validated: Boolean(worker.validated),
  })),
];

// What the payload sends of a link: type, id and a contact's companyId are the
// contract; label, sub and validated are ignored by the api, the demo's record
// pages need them.
export const linkPayload = (link) => ({
  type: link.type,
  id: link.id,
  label: link.label,
  ...(link.sub ? { sub: link.sub } : {}),
  ...(link.type === 'contact' && link.companyId
    ? { companyId: link.companyId }
    : {}),
  ...(link.type === 'tempWorker' ? { validated: Boolean(link.validated) } : {}),
});
